import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { t } from "i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
// Icons (import direct également)
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CodeIcon from "@mui/icons-material/Code";
import MovieIcon from "@mui/icons-material/Movie";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDButton from "components/Basics/MDButton";
import FolderSelectDropdown from "./FolderSelectDropdown";
import { selectAssetsByAssetTypes } from "../../../../../../../redux-react/reducers/assetReducer";
import { getGroupeTypeFile } from "./utils";

const FILE_TYPE_ICONS = {
	pdf: { icon: PictureAsPdfIcon, color: "error" },
	jpg: { icon: ImageIcon, color: "primary" },
	png: { icon: ImageIcon, color: "primary" },
	gif: { icon: ImageIcon, color: "primary" },
	jpeg: { icon: ImageIcon, color: "primary" },
	docx: { icon: DescriptionIcon, color: "primary" },
	doc: { icon: DescriptionIcon, color: "primary" },
	pptx: { icon: InsertDriveFileIcon, color: "warning" },
	ppt: { icon: InsertDriveFileIcon, color: "warning" },
	js: { icon: CodeIcon, color: "success" },
	html: { icon: CodeIcon, color: "success" },
	css: { icon: CodeIcon, color: "success" },
	mp4: { icon: MovieIcon, color: "secondary" },
	avi: { icon: MovieIcon, color: "secondary" },
	mov: { icon: MovieIcon, color: "secondary" },
	mp3: { icon: AudioFileIcon, color: "info" },
	wav: { icon: AudioFileIcon, color: "info" },
	default: { icon: InsertDriveFileIcon, color: "inherit" }
};

const FILTER_OPTIONS = [
	{ value: "all", label: "SOURCES.DOCUMENT_DIALOG.filterOptions.all" },
	{ value: "images", label: "SOURCES.DOCUMENT_DIALOG.filterOptions.images" },
	{ value: "documents", label: "SOURCES.DOCUMENT_DIALOG.filterOptions.documents" },
	{ value: "pdf", label: "SOURCES.DOCUMENT_DIALOG.filterOptions.pdf" },
	{ value: "word", label: "SOURCES.DOCUMENT_DIALOG.filterOptions.word" },
	{ value: "media", label: "SOURCES.DOCUMENT_DIALOG.filterOptions.media" }
];

const FileIcon = React.memo(({ fileType }) => {
	const { icon: IconComponent, color } = FILE_TYPE_ICONS[fileType] || FILE_TYPE_ICONS.default;
	return <IconComponent color={color} />;
});

const DocumentListItem = React.memo(({ doc, isSelected, copiedUrl, onSelect, onCopy, onOpen }) => {
	const handleCopy = useCallback(
		e => {
			e.stopPropagation();
			onCopy(doc.id, doc.url);
		},
		[doc.id, doc.url, onCopy]
	);

	const handleOpen = useCallback(
		e => {
			e.stopPropagation();
			onOpen(doc.url);
		},
		[doc.url, onOpen]
	);

	return (
		<ListItem
			button
			onClick={() => onSelect(doc.id)}
			selected={isSelected}
			sx={{ borderRadius: 1, mb: 0.5, px: 2 }}
		>
			<ListItemIcon>
				<Radio edge="start" checked={isSelected} tabIndex={-1} disableRipple />
			</ListItemIcon>
			<ListItemIcon>
				<FileIcon fileType={doc.type} />
			</ListItemIcon>
			<ListItemText
				primary={
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Typography variant="body1" component="span">
							{doc.name}
						</Typography>
						<LinkIcon fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />
					</Box>
				}
				secondary={
					<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
						<Typography variant="body2" component="span" sx={{ mr: 2 }}>
							{`${doc.type} • ${doc.size} `}
						</Typography>
						<Tooltip title={doc.url || ""}>
							<Typography
								variant="body2"
								component="span"
								sx={{
									color: "text.secondary",
									maxWidth: "250px",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									display: "inline-block"
								}}
							>
								{doc.url || ""}
							</Typography>
						</Tooltip>
					</Box>
				}
			/>
			<Tooltip title={t("SOURCES.DOCUMENT_DIALOG.copy")}>
				<IconButton edge="end" size="small" onClick={handleCopy} sx={{ mr: 1 }}>
					{copiedUrl === doc.id ? (
						<Typography variant="caption" color="success.main">
							{t("SOURCES.DOCUMENT_DIALOG.copied")}
						</Typography>
					) : (
						<ContentCopyIcon fontSize="small" />
					)}
				</IconButton>
			</Tooltip>
			<Tooltip title={t("SOURCES.DOCUMENT_DIALOG.open")}>
				<IconButton edge="end" size="small" onClick={handleOpen}>
					<OpenInNewIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</ListItem>
	);
});

const DocumentSelectionDialog = ({
	onClose,
	onConfirmSelect,
	codeLanguage,
	codeLanguagePosibles
}) => {
	const documentsSelector = useSelector(state =>
		selectAssetsByAssetTypes(state, ["images", "videos", "documents", "css"])
	);

	const [selectedDocument, setSelectedDocument] = useState(null);
	const [filterType, setFilterType] = useState("all");
	const [searchTerm, setSearchTerm] = useState("");
	const [copiedUrl, setCopiedUrl] = useState(null);
	const [selectedFolderId, setSelectedFolderId] = useState(null);
	const [folderIdsToInclude, setFolderIdsToInclude] = useState(null);

	const handleFolderSelection = (folderId, folderIds) => {
		setSelectedFolderId(folderId);
		setFolderIdsToInclude(folderIds);
	};
	// Format file size to be human-readable
	const formatFileSize = bytes => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const formatted = useMemo(() => {
		if (!documentsSelector) return [];

		return documentsSelector
			.filter(doc => {
				if (!folderIdsToInclude || folderIdsToInclude.length === 0) {
					return true;
				}
				return doc?.folderId && folderIdsToInclude.includes(doc.folderId);
			})
			.flatMap(doc => {
				const attachments = doc?.content?.[0]?.[codeLanguage]?.attachments || [];

				return attachments;
			})
			.map((item, index) => {
				const match = item?.contentType?.match(/\/([^/+]+)/);
				const fileType = match?.[1] || "unknown";
				return {
					id: index + 1,
					name: item.name || "Unnamed",
					type: fileType,
					size: formatFileSize(item.size),
					url: item.url || ""
				};
			});
	}, [documentsSelector, folderIdsToInclude, codeLanguagePosibles, codeLanguage]);

	const filteredDocuments = useMemo(() => {
		if (!formatted) return [];

		let filtered = formatted;

		if (filterType !== "all") {
			filtered = formatted.filter(doc => {
				switch (filterType) {
					case "images":
						return ["jpg", "png", "gif", "jpeg"].includes(doc.type);
					case "documents":
						return ["pdf", "docx", "doc", "pptx", "ppt"].includes(doc.type);
					case "pdf":
						return doc.type === "pdf";
					case "word":
						return ["docx", "doc"].includes(doc.type);
					case "media":
						return ["mp3", "mp4", "avi", "mov", "wav"].includes(doc.type);
					default:
						return true;
				}
			});
		}

		if (searchTerm) {
			const term = searchTerm.toLowerCase();
			filtered = filtered.filter(
				doc => doc?.name?.toLowerCase().includes(term) || doc?.url?.toLowerCase().includes(term)
			);
		}

		return filtered;
	}, [formatted, filterType, searchTerm]);

	const selectedDocumentDetails = useMemo(
		() => formatted?.find(doc => doc.id === selectedDocument),
		[selectedDocument, formatted]
	);

	const handleCopyUrl = useCallback((docId, url) => {
		navigator.clipboard.writeText(url).then(() => {
			setCopiedUrl(docId);
			setTimeout(() => setCopiedUrl(null), 2000);
		});
	}, []);

	const handleOpenUrl = useCallback(url => {
		window.open(url, "_blank");
	}, []);

	const handleConfirm = useCallback(() => {
		if (selectedDocumentDetails) {
			onConfirmSelect?.(selectedDocumentDetails, getGroupeTypeFile(selectedDocumentDetails?.type));
		}
	}, [selectedDocumentDetails, onConfirmSelect]);

	return (
		<Dialog
			open
			onClose={() => onClose(null)}
			fullWidth
			maxWidth="lg"
			slotProps={{
				paper: { sx: { height: "90%" } }
			}}
		>
			<DialogTitle>
				<Typography variant="h6">{t("SOURCES.DOCUMENT_DIALOG.title")}</Typography>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ mb: 2, display: "flex", gap: 2, marginTop: 1 }}>
					<FolderSelectDropdown
						onFolderIdSelect={handleFolderSelection}
						label={t("SOURCES.FOLDER.selectFolder")}
						selectedFolderId={selectedFolderId}
						includeSubfolders
					/>
					<FormControl sx={{ minWidth: 200 }}>
						<InputLabel>{t("SOURCES.DOCUMENT_DIALOG.filterLabel")}</InputLabel>
						<Select
							value={filterType}
							onChange={e => setFilterType(e.target.value)}
							label={t("SOURCES.DOCUMENT_DIALOG.filterLabel")}
							endAdornment={<ArrowDropDownIcon />}
						>
							{FILTER_OPTIONS.map(option => (
								<MenuItem key={option.value} value={option.value}>
									{t(option.label)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label={t("SOURCES.DOCUMENT_DIALOG.search")}
						variant="outlined"
						fullWidth
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
					/>
				</Box>

				{selectedDocumentDetails && (
					<SelectedDocumentPreview
						document={selectedDocumentDetails}
						copiedUrl={copiedUrl}
						onCopy={handleCopyUrl}
					/>
				)}

				<Divider sx={{ my: 2 }} />

				<DocumentList
					documents={filteredDocuments}
					selectedDocument={selectedDocument}
					copiedUrl={copiedUrl}
					onSelect={setSelectedDocument}
					onCopy={handleCopyUrl}
					onOpen={handleOpenUrl}
				/>
			</DialogContent>
			<DialogActions>
				<MDButton onClick={() => onClose(null)} variant="outlined" color="info">
					{t("SOURCES.DOCUMENT_DIALOG.cancel")}
				</MDButton>
				<MDButton onClick={handleConfirm} color="info" variant="contained">
					{t("SOURCES.DOCUMENT_DIALOG.confirm")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

const SelectedDocumentPreview = React.memo(({ document, copiedUrl, onCopy }) => (
	<Box sx={{ mb: 2 }}>
		<Typography variant="subtitle1">{t("SOURCES.DOCUMENT_DIALOG.selected")}:</Typography>
		<Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
			<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
				<Box sx={{ mr: 2 }}>
					<FileIcon fileType={document.type} />
				</Box>
				<Typography variant="body1" fontWeight="medium">
					{document.name}
				</Typography>
			</Box>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				{`${document.type} • ${document.size} `}
			</Typography>
			<UrlPreview url={document.url} docId={document.id} copiedUrl={copiedUrl} onCopy={onCopy} />
		</Paper>
	</Box>
));

const UrlPreview = React.memo(({ url, docId, copiedUrl, onCopy }) => (
	<Box sx={{ display: "flex", alignItems: "center" }}>
		<LinkIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
		<Link
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			sx={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis" }}
		>
			<Tooltip title={url}>
				<Typography
					variant="body2"
					component="span"
					sx={{
						color: "text.secondary",
						maxWidth: "80%",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						display: "inline-block"
					}}
				>
					{url}
				</Typography>
			</Tooltip>
		</Link>
		<Tooltip title={t("SOURCES.DOCUMENT_DIALOG.copy")}>
			<IconButton size="small" onClick={() => onCopy(docId, url)}>
				{copiedUrl === docId ? (
					<Typography variant="caption" color="success.main">
						{t("SOURCES.DOCUMENT_DIALOG.copied")}
					</Typography>
				) : (
					<ContentCopyIcon fontSize="small" />
				)}
			</IconButton>
		</Tooltip>
	</Box>
));

const DocumentList = React.memo(({ documents, ...props }) => (
	<List>
		{documents.length > 0 ? (
			documents.map(doc => (
				<DocumentListItem
					key={doc.id}
					doc={doc}
					isSelected={props.selectedDocument === doc.id}
					{...props}
				/>
			))
		) : (
			<Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
				{t("SOURCES.DOCUMENT_DIALOG.noneFound")}
			</Typography>
		)}
	</List>
));

export default React.memo(DocumentSelectionDialog);
